// src/components/tuition/TuitionTeacherPortal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChalkboardTeacher,
  FaUserGraduate,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaClipboardList,
  FaAward,
  FaVideo,
  FaPlus,
  FaSave,
  FaCalendarAlt,
  FaWhatsapp,
  FaPhone,
  FaCopy,
  FaPlay,
  FaSearch,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../../context/AppThemeContext";
import { useTenant } from "../../context/TenantContext";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import {
  getStoredStudents,
  saveStoredStudents,
  getStoredTeachers,
  getStoredOnlineClasses,
  saveStoredOnlineClasses,
  getStoredTuitionInstitutes,
  TUITION_MODULE_KEYS,
} from "./tuitionMockData";
import { normalizeVisibleOnlineClasses } from "./tuitionOnlineClass";
import ClassroomManagementStudio from "./ClassroomManagementStudio";

export default function TuitionTeacherPortal() {
  const { isDarkMode } = useAppTheme();
  const { activeTenant, tenantFeatures } = useTenant();
  const navigate = useNavigate();

  // Load tuition institutes and teachers
  const currentInstitute = activeTenant;
  const [allTeachers, setAllTeachers] = useState([]);

  // Current logged-in teacher
  const [selectedTeacherId, setSelectedTeacherId] = useState(allTeachers[0]?.id || "");
  const currentTeacher = useMemo(() => {
    return allTeachers.find((t) => t.id === selectedTeacherId) || allTeachers[0];
  }, [allTeachers, selectedTeacherId]);

  // Read enabled modules for this tuition
  const enabledModules = tenantFeatures?.enabledModules || currentInstitute?.enabledModules || {};
  const studentsEnabled = enabledModules[TUITION_MODULE_KEYS.STUDENT_MANAGEMENT] === true
    || enabledModules[TUITION_MODULE_KEYS.ATTENDANCE] === true
    || enabledModules[TUITION_MODULE_KEYS.ASSESSMENTS] === true;
  const teachersEnabled = enabledModules[TUITION_MODULE_KEYS.TEACHER_MANAGEMENT] === true;
  const onlineClassesEnabled = enabledModules[TUITION_MODULE_KEYS.ONLINE_CLASSES] === true;

  // State for students & online classes
  const [students, setStudents] = useState([]);
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [activeStudioClass, setActiveStudioClass] = useState(null);

  useEffect(() => {
    let active = true;
    const list = (response, keys) => {
      const payload = response?.data?.data ?? response?.data;
      if (Array.isArray(payload)) return payload;
      for (const key of keys) if (Array.isArray(payload?.[key])) return payload[key];
      return [];
    };
    Promise.all([
      studentsEnabled ? api.get(API_PATHS.tuition.students()) : Promise.resolve(null),
      teachersEnabled ? api.get(API_PATHS.tuition.teachers()) : Promise.resolve(null),
      onlineClassesEnabled ? api.get(API_PATHS.tuition.onlineClasses()) : Promise.resolve(null),
    ]).then(([studentResponse, teacherResponse, classResponse]) => {
      if (!active) return;
      setStudents(list(studentResponse, ["students", "items"]));
      setAllTeachers(list(teacherResponse, ["teachers", "items"]));
      setOnlineClasses(normalizeVisibleOnlineClasses(list(classResponse, ["rooms", "classes", "items"])));
    }).catch((error) => toast.error(error?.message || "Unable to load faculty workspace data."));
    return () => { active = false; };
  }, [studentsEnabled, teachersEnabled, onlineClassesEnabled]);

  // Selected Batch filter
  const [selectedBatch, setSelectedBatch] = useState("");
  const batchOptions = useMemo(
    () => [...new Set((students || []).map((student) => student.batch).filter(Boolean))],
    [students]
  );
  useEffect(() => {
    if (!selectedBatch && batchOptions.length) setSelectedBatch(batchOptions[0]);
  }, [batchOptions, selectedBatch]);
  const [activeTab, setActiveTab] = useState("attendance"); // "attendance" | "assessments" | "students" | "online-classes"

  // -------------------------------------------------------------
  // ATTENDANCE STATE & LOGIC
  // -------------------------------------------------------------
  const [attendanceDate, setAttendanceDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [attendanceMap, setAttendanceMap] = useState({});

  // Filter students enrolled in current batch
  const batchStudents = useMemo(() => {
    return (students || []).filter((s) => s && s.batch === selectedBatch);
  }, [students, selectedBatch]);

  // Initialize attendance map for batch
  useEffect(() => {
    const initial = {};
    batchStudents.forEach((s) => {
      initial[s.id] = initial[s.id] || "PRESENT";
    });
    setAttendanceMap(initial);
  }, [batchStudents]);

  const setStudentAttendance = (studentId, status) => {
    setAttendanceMap((prev) => ({ ...prev, [studentId]: status }));
  };

  const markAll = (status) => {
    const updated = {};
    batchStudents.forEach((s) => {
      updated[s.id] = status;
    });
    setAttendanceMap(updated);
    toast.info(`Marked all students as ${status}.`);
  };

  const saveBatchAttendance = () => {
    toast.success(`Batch attendance saved for ${selectedBatch} on ${attendanceDate}!`);
  };

  // -------------------------------------------------------------
  // ASSESSMENTS & GRADING STATE & LOGIC
  // -------------------------------------------------------------
  const [assessments, setAssessments] = useState([]);

  const [activeTestId, setActiveTestId] = useState("");
  const activeTest = useMemo(() => assessments.find((t) => t.id === activeTestId) || assessments[0], [assessments, activeTestId]);
  const [currentMarksMap, setCurrentMarksMap] = useState(activeTest?.marksMap || {});
  const [createTestModal, setCreateTestModal] = useState(false);
  const [newTestForm, setNewTestForm] = useState({
    title: "",
    subject: "",
    maxMarks: 50,
    date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (activeTest) {
      setCurrentMarksMap(activeTest.marksMap || {});
    }
  }, [activeTest]);

  const handleMarkChange = (studentId, value) => {
    const num = Math.min(Number(activeTest?.maxMarks || 100), Math.max(0, Number(value)));
    setCurrentMarksMap((prev) => ({ ...prev, [studentId]: num }));
  };

  const computeGrade = (marks, maxMarks) => {
    if (marks === undefined || marks === "") return { grade: "-", tone: "text-slate-400" };
    const pct = (Number(marks) / Number(maxMarks)) * 100;
    if (pct >= 90) return { grade: "A+", tone: "text-emerald-500 font-bold" };
    if (pct >= 80) return { grade: "A", tone: "text-emerald-600 font-bold" };
    if (pct >= 70) return { grade: "B+", tone: "text-sky-600 font-bold" };
    if (pct >= 60) return { grade: "B", tone: "text-sky-500 font-semibold" };
    if (pct >= 50) return { grade: "C", tone: "text-amber-500 font-semibold" };
    return { grade: "F", tone: "text-rose-500 font-bold" };
  };

  const saveMarks = () => {
    setAssessments((prev) =>
      prev.map((test) => {
        if (test.id === activeTestId) {
          return { ...test, marksMap: currentMarksMap };
        }
        return test;
      })
    );
    toast.success(`Assessment marks saved and grades computed for ${activeTest?.title}!`);
  };

  const handleCreateTest = (e) => {
    e.preventDefault();
    if (!newTestForm.title.trim()) {
      toast.error("Test title is required.");
      return;
    }
    const newTest = {
      id: `test-${Date.now()}`,
      title: newTestForm.title.trim(),
      subject: newTestForm.subject,
      maxMarks: Number(newTestForm.maxMarks || 50),
      date: newTestForm.date,
      marksMap: {},
    };
    setAssessments([newTest, ...assessments]);
    setActiveTestId(newTest.id);
    setCreateTestModal(false);
    toast.success(`Assessment '${newTest.title}' created! You can now enter student marks.`);
  };

  // -------------------------------------------------------------
  // ONLINE CLASSES LOGIC
  // -------------------------------------------------------------
  const [scheduleModal, setScheduleModal] = useState(false);
  const [classTopic, setClassTopic] = useState("");
  const [classSubject, setClassSubject] = useState("");
  const [classStartTime, setClassStartTime] = useState("");
  const [classEndTime, setClassEndTime] = useState("");

  const handleScheduleOnlineClass = (e) => {
    e.preventDefault();
    if (!classTopic.trim()) {
      toast.error("Please provide a lecture topic.");
      return;
    }

    const meetingId = `NX-${classSubject.substring(0, 4).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`;

    const newClass = {
      id: `cls-${Date.now()}`,
      tuitionId: currentInstitute?.id,
      meetingId,
      topic: classTopic.trim(),
      subject: classSubject,
      batch: selectedBatch,
      teacherName: currentTeacher?.name,
      scheduledDate: new Date().toISOString().split("T")[0],
      startTime: classStartTime,
      endTime: classEndTime,
      status: "scheduled",
      activeParticipantsCount: 0,
      attendees: [],
    };

    const updated = [newClass, ...onlineClasses];
    setOnlineClasses(updated);
    saveStoredOnlineClasses(updated);
    setScheduleModal(false);
    setClassTopic("");
    toast.success(`Live Lecture scheduled! Direct Meeting Code: ${meetingId}`);
  };

  const handleDeleteOnlineClass = async (onlineClass) => {
    if (onlineClass.status === "live") {
      toast.error("End the live meeting before deleting it");
      return;
    }
    if (!window.confirm(`Delete ${onlineClass.topic || "this online class"}? Attendance records will be preserved.`)) return;

    try {
      await api.delete(API_PATHS.tuition.deleteOnlineClass(onlineClass.meetingId));
      setOnlineClasses((current) => current.filter((item) => item.meetingId !== onlineClass.meetingId));
      toast.success("Online class deleted.");
    } catch (error) {
      toast.error(error?.message || "Unable to delete the online class.");
    }
  };

  // -------------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------------
  if (activeStudioClass) {
    return (
      <ClassroomManagementStudio
        roomCode={activeStudioClass.meetingId}
        scheduledClass={activeStudioClass}
        isHost={true}
        facultyName={currentTeacher?.name}
        onClose={() => {
          setActiveStudioClass(null);
          if (onlineClassesEnabled) {
            api
              .get(API_PATHS.tuition.onlineClasses())
              .then((res) => {
                const payload = res?.data?.data ?? res?.data;
                const list = Array.isArray(payload) ? payload : payload?.rooms || payload?.classes || [];
                setOnlineClasses(normalizeVisibleOnlineClasses(list));
              })
              .catch(() => {});
          }
        }}
      />
    );
  }

  return (
    <div className={`min-h-screen pb-16 pt-4 px-3 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Teacher Profile Banner */}
        <div className={`rounded-3xl border p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-tr from-indigo-600 to-sky-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-indigo-600/20">
              <FaChalkboardTeacher />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-indigo-100 dark:bg-indigo-950/60 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                  Faculty Workspace
                </span>
                <span className="text-xs text-slate-400 font-semibold">
                  {currentInstitute?.name}
                </span>
              </div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100 mt-1">
                {currentTeacher?.name}
              </h1>
              <p className="text-xs text-slate-500">
                Specialization: {Array.isArray(currentTeacher?.specialization) ? currentTeacher?.specialization.join(", ") : currentTeacher?.specialization}
              </p>
            </div>
          </div>

          {/* Batch Selector & Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                Selected Batch:
              </label>
              <select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                className={`rounded-2xl border px-3 py-2 text-xs font-bold focus:outline-none focus:border-indigo-500 ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-100" : "border-slate-300 bg-slate-50 text-slate-900"}`}
              >
                {!batchOptions.length && <option value="">No batches available</option>}
                {batchOptions.map((batch) => <option key={batch} value={batch}>{batch}</option>)}
              </select>
            </div>

            {enabledModules[TUITION_MODULE_KEYS.ONLINE_CLASSES] && (
              <button
                type="button"
                onClick={() => navigate("/tuition/classes/new")}
                className="mt-4 inline-flex items-center gap-1.5 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2.5 text-xs font-bold text-white shadow-md hover:opacity-90 transition"
              >
                <FaVideo /> Schedule Live Class
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
          {enabledModules[TUITION_MODULE_KEYS.ATTENDANCE] && (
            <button
              type="button"
              onClick={() => setActiveTab("attendance")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "attendance" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              <FaClipboardList /> Mark Batch Attendance
            </button>
          )}

          {enabledModules[TUITION_MODULE_KEYS.ASSESSMENTS] && (
            <button
              type="button"
              onClick={() => setActiveTab("assessments")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "assessments" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              <FaAward /> Assessments & Grading
            </button>
          )}

          <button
            type="button"
            onClick={() => setActiveTab("students")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "students" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
          >
            <FaUserGraduate /> Enrolled Students & Parents ({batchStudents.length})
          </button>

          {enabledModules[TUITION_MODULE_KEYS.ONLINE_CLASSES] && (
            <button
              type="button"
              onClick={() => setActiveTab("online-classes")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "online-classes" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              <FaVideo /> Online Classes & Rooms
            </button>
          )}
        </div>

        {/* -------------------------------------------------------------
            TAB 1: BATCH ATTENDANCE MARKING
        ------------------------------------------------------------- */}
        {activeTab === "attendance" && (
          <div className="space-y-4">
            {/* Attendance Header Controls */}
            <div className={`rounded-3xl border p-5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <FaCalendarAlt className="text-slate-400 text-sm" />
                  <span className="text-xs font-bold text-slate-400 uppercase">Date:</span>
                  <input
                    type="date"
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className={`rounded-xl border px-3 py-1.5 text-xs font-bold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-slate-50 text-slate-900"}`}
                  />
                </div>
              </div>

              {/* Bulk actions */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => markAll("PRESENT")}
                  className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 transition"
                >
                  Mark All Present
                </button>
                <button
                  type="button"
                  onClick={() => markAll("ABSENT")}
                  className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-500/20 transition"
                >
                  Mark All Absent
                </button>
                <button
                  type="button"
                  onClick={saveBatchAttendance}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-sky-700 transition shadow-sm"
                >
                  <FaSave /> Save Attendance
                </button>
              </div>
            </div>

            {/* Attendance Roster Table */}
            <div className={`overflow-x-auto rounded-3xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <table className="w-full text-left text-xs">
                <thead className={`border-b text-[11px] font-bold uppercase tracking-wider text-slate-400 ${isDarkMode ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                  <tr>
                    <th className="px-4 py-3.5">Roll No</th>
                    <th className="px-4 py-3.5">Student Name</th>
                    <th className="px-4 py-3.5">Overall Attendance</th>
                    <th className="px-4 py-3.5 text-right">Status on {attendanceDate}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {batchStudents.map((s) => {
                    const currentStatus = attendanceMap[s.id] || "PRESENT";
                    return (
                      <tr key={s.id} className="hover:bg-slate-500/5 transition">
                        <td className="px-4 py-3 font-mono font-bold text-slate-400">{s.rollNo}</td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-slate-900 dark:text-slate-100">{s.name}</p>
                          <p className="text-[10px] text-slate-400">{s.grade}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="font-extrabold text-sky-500">{Number(s.attendanceRate || 0)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex rounded-xl border border-slate-300 dark:border-slate-700 p-0.5 bg-slate-100 dark:bg-slate-950">
                            <button
                              type="button"
                              onClick={() => setStudentAttendance(s.id, "PRESENT")}
                              className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition ${currentStatus === "PRESENT" ? "bg-emerald-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
                            >
                              Present
                            </button>
                            <button
                              type="button"
                              onClick={() => setStudentAttendance(s.id, "LATE")}
                              className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition ${currentStatus === "LATE" ? "bg-amber-500 text-slate-950 font-black shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
                            >
                              Late
                            </button>
                            <button
                              type="button"
                              onClick={() => setStudentAttendance(s.id, "ABSENT")}
                              className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition ${currentStatus === "ABSENT" ? "bg-rose-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
                            >
                              Absent
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 2: ASSESSMENTS & MARKS ENTRY
        ------------------------------------------------------------- */}
        {activeTab === "assessments" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <span className="text-xs font-bold text-slate-400 uppercase">Assessment:</span>
                <select
                  value={activeTestId}
                  onChange={(e) => setActiveTestId(e.target.value)}
                  className={`rounded-2xl border px-3 py-2 text-xs font-bold focus:outline-none ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                >
                  {assessments.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title} (Max: {t.maxMarks} Marks)
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => navigate("/tuition/assessments/new")}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3.5 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                >
                  <FaPlus /> New Test
                </button>
                <button
                  type="button"
                  onClick={saveMarks}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-2 text-xs font-bold text-white hover:bg-sky-700 transition shadow-sm"
                >
                  <FaSave /> Save Grades
                </button>
              </div>
            </div>

            {/* Marks & Grade Entry Table */}
            <div className={`overflow-x-auto rounded-3xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-sm">{activeTest?.title}</h3>
                  <p className="text-xs text-slate-400">Date: {activeTest?.date} • Maximum Marks: {activeTest?.maxMarks}</p>
                </div>
                <span className="text-xs font-bold text-emerald-500">Grading System: Standard Percentage</span>
              </div>

              <table className="w-full text-left text-xs">
                <thead className={`border-b text-[11px] font-bold uppercase tracking-wider text-slate-400 ${isDarkMode ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                  <tr>
                    <th className="px-4 py-3">Roll No</th>
                    <th className="px-4 py-3">Student Name</th>
                    <th className="px-4 py-3">Marks Scored (out of {activeTest?.maxMarks})</th>
                    <th className="px-4 py-3">Percentage</th>
                    <th className="px-4 py-3">Assigned Grade</th>
                    <th className="px-4 py-3">Teacher Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {batchStudents.map((s) => {
                    const marks = currentMarksMap[s.id] !== undefined ? currentMarksMap[s.id] : "";
                    const pct = marks !== "" ? Math.round((Number(marks) / Number(activeTest?.maxMarks || 100)) * 100) : "-";
                    const gradeInfo = computeGrade(marks, activeTest?.maxMarks);

                    return (
                      <tr key={s.id} className="hover:bg-slate-500/5 transition">
                        <td className="px-4 py-3 font-mono font-bold text-slate-400">{s.rollNo}</td>
                        <td className="px-4 py-3 font-bold text-slate-900 dark:text-slate-100">{s.name}</td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            max={activeTest?.maxMarks || 100}
                            value={marks}
                            onChange={(e) => handleMarkChange(s.id, e.target.value)}
                            placeholder="0"
                            className={`w-24 rounded-xl border px-3 py-1.5 text-xs font-bold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                          />
                        </td>
                        <td className="px-4 py-3 font-semibold">{pct !== "-" ? `${pct}%` : "-"}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-lg px-2.5 py-0.5 text-xs ${gradeInfo.tone}`}>
                            {gradeInfo.grade}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder="e.g. Excellent conceptual grasp"
                            className={`w-full max-w-xs rounded-xl border px-3 py-1 text-xs focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 3: ENROLLED STUDENTS & PARENT CONTACT INFO
        ------------------------------------------------------------- */}
        {activeTab === "students" && (
          <div className="space-y-4">
            <div className={`overflow-x-auto rounded-3xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="p-4 border-b border-slate-200 dark:border-slate-800">
                <h3 className="font-bold text-sm">Batch Students & Emergency Contacts</h3>
                <p className="text-xs text-slate-400">Direct contact info for parent consultations</p>
              </div>

              <table className="w-full text-left text-xs">
                <thead className={`border-b text-[11px] font-bold uppercase tracking-wider text-slate-400 ${isDarkMode ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                  <tr>
                    <th className="px-4 py-3">Student Name</th>
                    <th className="px-4 py-3">Parent Name</th>
                    <th className="px-4 py-3">Parent Mobile</th>
                    <th className="px-4 py-3">Fee Status</th>
                    <th className="px-4 py-3 text-right">Quick Contact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {batchStudents.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-500/5 transition">
                      <td className="px-4 py-3 font-bold text-slate-900 dark:text-slate-100">
                        {s.name} <span className="font-mono text-slate-400 text-[10px]">({s.rollNo})</span>
                      </td>
                      <td className="px-4 py-3 font-semibold">{s.parentName} ({s.parentRelation || "Parent"})</td>
                      <td className="px-4 py-3 font-mono">{s.parentMobile}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${s.feeStatus === "PAID" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"}`}>
                          {s.feeStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <a
                          href={`https://api.whatsapp.com/send?phone=${s.parentMobile.replace(/[^0-9]/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700 transition"
                        >
                          <FaWhatsapp /> Chat with Parent
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 4: SCHEDULE & CONDUCT ONLINE LIVE CLASSROOMS
        ------------------------------------------------------------- */}
        {activeTab === "online-classes" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold">Faculty Live Class Sessions</h3>
                <p className="text-xs text-slate-500">
                  Share meeting IDs with your students. Students can join without login via the Direct Meeting Join UI!
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/tuition/classes/new")}
                className="rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2.5 text-xs font-bold text-white shadow-md hover:opacity-90 transition"
              >
                <FaVideo /> Schedule Lecture
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {onlineClasses.map((cls) => (
                <div
                  key={cls.id}
                  className={`rounded-3xl border p-5 shadow-sm flex flex-col justify-between ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase ${cls.status === "live" ? "bg-emerald-500/20 text-emerald-500 animate-pulse" : "bg-sky-500/20 text-sky-500"}`}>
                        {cls.status === "live" ? "● LIVE CLASSROOM" : "SCHEDULED"}
                      </span>
                      <span className="text-xs font-bold text-slate-400">{cls.subject}</span>
                    </div>

                    <h4 className="font-bold text-base mt-2 text-slate-900 dark:text-slate-100">{cls.topic}</h4>
                    <p className="text-xs text-slate-500 mt-1">Batch: {cls.batch}</p>

                    <div className={`mt-4 rounded-2xl border p-3 text-xs flex items-center justify-between ${isDarkMode ? "border-slate-800 bg-slate-950/60" : "border-slate-100 bg-slate-50"}`}>
                      <div>
                        <span className="text-[10px] font-bold uppercase text-slate-400">Direct Meeting ID:</span>
                        <p className="font-mono font-bold text-sm text-sky-500">{cls.meetingId}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(cls.meetingId);
                          toast.success(`Meeting code ${cls.meetingId} copied to clipboard!`);
                        }}
                        className="rounded-lg border border-slate-300 dark:border-slate-700 p-2 text-xs hover:bg-slate-200 dark:hover:bg-slate-800 transition"
                        title="Copy Code"
                      >
                        <FaCopy />
                      </button>
                    </div>

                    <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5">
                      <FaClock /> {cls.scheduledDate} ({cls.startTime} - {cls.endTime})
                    </div>
                  </div>

                  <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveStudioClass(cls)}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 transition shadow"
                    >
                      <FaPlay /> Start / Enter Room
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const directUrl = `${window.location.origin}/classroom/join/${cls.meetingId}`;
                        navigator.clipboard?.writeText(directUrl);
                        toast.success("Student Direct Join link copied!");
                      }}
                      className="ml-auto text-xs font-semibold text-sky-500 hover:underline"
                    >
                      Copy Student Join Link
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeleteOnlineClass(cls)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-500 hover:text-rose-600 transition"
                      title={cls.status === "live" ? "End the live meeting before deleting it" : "Delete online class"}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* -------------------------------------------------------------
          MODAL: SCHEDULE LIVE LECTURE
      ------------------------------------------------------------- */}
      {scheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-xl font-black flex items-center gap-2">
              <FaVideo className="text-violet-500" /> Schedule Live Lecture
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              ClassNX will generate a meeting ID that students can join directly without login.
            </p>

            <form onSubmit={handleScheduleOnlineClass} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Lecture Topic *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Wave Optics & Interference"
                  value={classTopic}
                  onChange={(e) => setClassTopic(e.target.value)}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Subject</label>
                  <input
                    type="text"
                    value={classSubject}
                    onChange={(e) => setClassSubject(e.target.value)}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Batch</label>
                  <input
                    type="text"
                    readOnly
                    value={selectedBatch}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold opacity-80 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-300" : "border-slate-300 bg-slate-100 text-slate-700"}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Start Time</label>
                  <input
                    type="time"
                    value={classStartTime}
                    onChange={(e) => setClassStartTime(e.target.value)}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">End Time</label>
                  <input
                    type="time"
                    value={classEndTime}
                    onChange={(e) => setClassEndTime(e.target.value)}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setScheduleModal(false)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-violet-600 px-5 py-2 text-xs font-bold text-white hover:bg-violet-700 transition"
                >
                  Create & Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------
          MODAL: CREATE ASSESSMENT / TEST
      ------------------------------------------------------------- */}
      {createTestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-xl font-black flex items-center gap-2">
              <FaAward className="text-sky-500" /> Create Unit Assessment
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Add a new test to record marks and compute student performance grades.
            </p>

            <form onSubmit={handleCreateTest} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Assessment Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Thermodynamics & Heat Transfer Mock"
                  value={newTestForm.title}
                  onChange={(e) => setNewTestForm({ ...newTestForm, title: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Subject</label>
                  <input
                    type="text"
                    value={newTestForm.subject}
                    onChange={(e) => setNewTestForm({ ...newTestForm, subject: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Max Marks</label>
                  <input
                    type="number"
                    value={newTestForm.maxMarks}
                    onChange={(e) => setNewTestForm({ ...newTestForm, maxMarks: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Test Date</label>
                <input
                  type="date"
                  value={newTestForm.date}
                  onChange={(e) => setNewTestForm({ ...newTestForm, date: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setCreateTestModal(false)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-sky-600 px-5 py-2 text-xs font-bold text-white hover:bg-sky-700 transition"
                >
                  Create Assessment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
